import { AuthProvider } from 'react-admin';
import { BASE_URL } from "./config";
import { jwtDecode } from 'jwt-decode';
import { DecodedToken } from './context/UserContext';

const authProvider: AuthProvider = {
    login: (data) => {
        const request = new Request(`${BASE_URL}/auth/user/login`, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: new Headers({ 'Content-Type': 'application/json' }),
        });
        return fetch(request).then(response => {
            if (response.status < 200 || response.status >= 300) {
                throw response.status;
            }
            return response.json();
        }).then((auth) => {
            const decoded = jwtDecode<DecodedToken>(auth.access_token);
            localStorage.setItem("access_token", auth.access_token);
            localStorage.setItem('permissions', JSON.stringify(decoded.permissions));

            // Trigger UserContext to refresh after successful login
            window.dispatchEvent(new StorageEvent('storage', {
                key: 'access_token',
                newValue: auth.access_token,
                oldValue: localStorage.getItem('access_token'),
                storageArea: localStorage
            }));

            // Redirect to main page after login
            return Promise.resolve('/');
        }).catch((error) => {            
            throw error;
        });

    },
    checkError: (error) => {
        const status = error.status;
        if (status === 401 || status === 403) {
            localStorage.removeItem("access_token");
            localStorage.removeItem('permissions');

            return Promise.reject({ redirectTo: '/login' });
        }
        return Promise.resolve();
    },
    checkAuth: () => {
        return localStorage.getItem("access_token") ? Promise.resolve() : Promise.reject();
    },
    logout: async () => {
        try {
            const access_token = localStorage.getItem('access_token');
            if (access_token) {
                await fetch(`${BASE_URL}/auth/user/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${access_token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }
        } catch (error) {
            console.error('Logout API call failed:', error);
        } finally {
            localStorage.removeItem('access_token');
            localStorage.removeItem('permissions');
        }

        // Let react-admin handle the redirect naturally
        return Promise.resolve('/login');
    },
    getPermissions: () => {
        const token = localStorage.getItem("access_token");
        
        if (!token) {
            return Promise.resolve(null);
        }
        
        try {
            // Decode the token to get the most current permissions
            const decoded = jwtDecode<DecodedToken>(token);
            
            // Check if token is expired
            const currentTime = Date.now() / 1000;
            if (decoded.exp && decoded.exp < currentTime) {
                localStorage.removeItem("access_token");
                localStorage.removeItem('permissions');
                return Promise.resolve(null);
            }
            
            // Update localStorage permissions with fresh data from token
            localStorage.setItem('permissions', JSON.stringify(decoded.permissions));
            
            return Promise.resolve(decoded.permissions);
        } catch (error) {
            console.error("Failed to decode token for permissions:", error);
            localStorage.removeItem("access_token");
            localStorage.removeItem('permissions');
            return Promise.resolve(null);
        }
    }
};

export default authProvider;